/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import FixedBanner from "../../components/FixedBanner/FixedBanner";
import WarningIcon from "@mui/icons-material/Warning";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import BoltIcon from "@mui/icons-material/Bolt";

import { environment } from "../../environments/environment";
import PostModal from "../../components/PostModal/PostModal";
import "./Dashboard.css";
import axios from "axios";
import { useAuthContext } from "../../hooks/useAuthContext";

function Dashboard() {
  document.title = `Dashboard | LeadZLab`;

  const [plan, setPlan] = useState("");
  const [trialValidUntil, setTrialValidUntil] = useState(false);
  const [quota, setQuota] = useState({});
  const [jobs, setJobs] = useState([]);
  const [jobLoader, setJobLoader] = useState(true);
  const [stats, setStats] = useState({
    totalLeads: 0,
    unreadLeads: 0,
    jobsCount: 0,
    leadsByDay: [],
  });
  const [statsLoader, setStatsLoader] = useState(true);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentLeadsLoader, setRecentLeadsLoader] = useState(true);
  const [recentLeadsFilter, setRecentLeadsFilter] = useState("recent"); // "recent" | "unread"
  const [expandedLeadId, setExpandedLeadId] = useState(null);

  const { user } = useAuthContext();
  const navigate = useNavigate();

  const getRecentLeads = async (filter) => {
    const unreadOnly = (filter ?? recentLeadsFilter) === "unread";
    try {
      const params = new URLSearchParams({ limit: "10", unread_only: String(unreadOnly) });
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/dashboard/recent-leads?${params}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setRecentLeads(data.data?.leads || []);
    } catch (err) {
      setRecentLeads([]);
    } finally {
      setRecentLeadsLoader(false);
    }
  };

  useEffect(() => {
    if (!user?.accessToken) return;
    setRecentLeadsLoader(true);
    getRecentLeads(recentLeadsFilter);
  }, [user?.accessToken, recentLeadsFilter]);

  useEffect(() => {
    if (!expandedLeadId) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpandedLeadId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedLeadId]);

  // Lock body scroll when modal is open (same as Job Detail / Execution Detail)
  useEffect(() => {
    if (!expandedLeadId) return;
    const scrollY = window.scrollY ?? window.pageYOffset;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevStyle = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    return () => {
      Object.assign(document.body.style, prevStyle);
      window.scrollTo(0, scrollY);
    };
  }, [expandedLeadId]);

  const getRelativeTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(Date.parse(dateString));
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return new Date(dateString).toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const markLeadRead = async (postId) => {
    if (!postId || !user?.accessToken) return;
    try {
      await axios.put(
        `${environment.BACKEND}/resources/post/read-status`,
        { postIds: [postId], is_read: true },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${user?.accessToken}` } }
      );
      setRecentLeads((prev) => prev.map((p) => (String(p._id) === String(postId) ? { ...p, is_read: true } : p)));
      setStats((prev) => ({ ...prev, unreadLeads: Math.max(0, (prev.unreadLeads || 0) - 1) }));
    } catch (err) {
      // ignore
    }
  };

  const getJobs = async () => {
    try {
      const now = new Date();
      const todayStr = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
      const tzOffsetMinutes = now.getTimezoneOffset();
      const params = new URLSearchParams({ dashboard: "true", todayDate: todayStr, tzOffsetMinutes: String(tzOffsetMinutes) });
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/job?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setJobs(data.data);
    } catch (err) {
      if (err.response?.status === 400) window.alert("Please login again");
    } finally {
      setJobLoader(false);
    }
  };

  const getStats = async () => {
    try {
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/dashboard/stats?days=14`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setStats(data.data);
    } catch (err) {
      // ignore
    } finally {
      setStatsLoader(false);
    }
  };

  function calculateDateDifference(timestamp) {
    const now = new Date();
    const targetDate = new Date(timestamp);
    if (targetDate < now) return -1;
    const timeDifference = targetDate - now;
    return Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  }

  const getPlan = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      const userData = data.data;
      setPlan(userData.plan);
      setTrialValidUntil(
        calculateDateDifference(userData.subscriptionActiveTill)
      );
    } catch (err) {
      // ignore
    }
  };

  const checkQuota = async () => {
    try {
      const { data } = await axios.get(`${environment.BACKEND}/user/quota`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });
      setQuota(data.data);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    getJobs();
    getStats();
    getPlan();
    checkQuota();
  }, []);

  const dateOpts = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  const [showFirstMatcherBanner, setShowFirstMatcherBanner] = useState(false);
  useEffect(() => {
    if (jobs.length > 0 && typeof window !== "undefined") {
      const seen = localStorage.getItem("leadzlab_first_matcher_seen");
      if (!seen) {
        setShowFirstMatcherBanner(true);
        localStorage.setItem("leadzlab_first_matcher_seen", "true");
      }
    }
  }, [jobs.length]);

  const formatChartDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T12:00:00");
    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "short" });
    const weekday = d.toLocaleString("en-US", { weekday: "short" });
    return `${day} ${month} (${weekday})`;
  };

  const chartData = (stats.leadsByDay || []).map((d) => ({
    ...d,
    shortDate: d.date ? formatChartDate(d.date) : "",
  }));

  const now = new Date();
  const todayStr = now.toISOString().split("T")[0];
  const dayToCount = (stats.leadsByDay || []).reduce((acc, d) => {
    acc[d.date] = d.count;
    return acc;
  }, {});
  const batchToday = dayToCount[todayStr] ?? 0;
  let batchLast7 = 0;
  for (let i = 0; i < 7; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().split("T")[0];
    batchLast7 += dayToCount[key] ?? 0;
  }

  return (
    <div className="dashboard-page dashboard-widgets">
      <header className="dashboard-header dashboard-header-compact">
        <div>
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">Review leads and scout activity.</p>
        </div>
        {!statsLoader && (
          <div className="dashboard-summary dashboard-summary-inline">
            <p className="dashboard-summary-text">
              <NavLink to="/recent" className="dashboard-summary-link">View recent</NavLink>
              <span className="dashboard-summary-sep">·</span>
              <NavLink to="/recent" className="dashboard-summary-link">{batchToday} today</NavLink>
              <span className="dashboard-summary-sep">·</span>
              <NavLink to="/jobs" className="dashboard-summary-link">{stats.jobsCount} scouts</NavLink>
              {quota?.postsLimit != null && (
                <>
                  <span className="dashboard-summary-sep">·</span>
                  <span className="dashboard-summary-text text-slate-600">
                    {quota.postsFoundToday ?? 0} / {quota.postsLimit} posts today
                  </span>
                </>
              )}
            </p>
            <NavLink to="/jobs" className="dashboard-quick-action-btn primary">View scouts</NavLink>
            <NavLink to="/create-job" className="dashboard-quick-action-btn secondary">+ New scout</NavLink>
          </div>
        )}
      </header>

      {/* Posts usage nudge: show when user has a limit and is approaching or at cap (not already showing quota-reached banner) */}
      {quota?.postsLimit != null && !quota?.postsQuotaReached && (quota.postsFoundToday ?? 0) >= Math.floor(quota.postsLimit * 0.8) && plan && plan !== "beta" && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            You&apos;ve used <strong>{quota.postsFoundToday} of {quota.postsLimit}</strong> posts today. Upgrade for more capacity.
          </p>
          <NavLink to="/pricing" className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700">
            Upgrade
          </NavLink>
        </div>
      )}

      {showFirstMatcherBanner && jobs.length > 0 && (
        <div className="dashboard-banner dashboard-banner-info rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <p className="font-medium">Your scout is running.</p>
          <p className="mt-0.5 text-emerald-700">
            New leads will appear under each scout. Check <strong>Alerts</strong> to get notified.
          </p>
        </div>
      )}

      {plan === "free" ? (
        trialValidUntil < 0 ? (
          <FixedBanner
            icon={<WarningIcon />}
            message="Your free trial has expired. Upgrade to continue generating leads."
            actionText="Upgrade"
            actionLink="/pricing"
            color="red"
          />
        ) : (
          <FixedBanner
            icon={<BoltIcon />}
            message={`Your free trial expires in ${trialValidUntil} days`}
            actionText="Upgrade"
            actionLink="/pricing"
            color="emerald"
          />
        )
      ) : plan === "basic" && quota?.postsQuotaReached !== true && quota?.jobsCreated < (quota?.jobsAllowed ?? 0) ? (
        <FixedBanner
          icon={<VerifiedUserIcon />}
          message="Basic plan (100 posts/day). Upgrade to Ultra for 500 posts per day."
          actionText="Upgrade to Ultra"
          actionLink="/pricing"
          color="yellow"
        />
      ) : plan === "" || plan === "basic" ? null : (
        <FixedBanner
          icon={<VerifiedUserIcon />}
          message={`You're on the ${plan?.toUpperCase()} plan`}
          color="yellow"
        />
      )}

      {quota?.jobsCreated >= quota?.jobsAllowed && (
        <FixedBanner
          icon={<PriorityHighIcon />}
          message={`Scout limit reached (${quota.jobsCreated}/${quota.jobsAllowed}). Upgrade to create more.`}
          actionText="Upgrade"
          actionLink="/pricing"
          color="gray"
        />
      )}

      {quota?.postsQuotaReached && (
        <FixedBanner
          icon={<PriorityHighIcon />}
          message="Your scouts have found your daily quota today. Want them to keep looking? Upgrade for more posts per day."
          actionText="Upgrade"
          actionLink="/pricing"
          color="gray"
        />
      )}

      {/* Widget grid: one view, no page scroll */}
      <div className="dashboard-widget-grid">
      {/* Recent leads widget */}
      <section className="dashboard-widget dashboard-widget-leads">
        <div className="dashboard-widget-head">
          <h2 className="dashboard-widget-title">Recent leads</h2>
          <div className="dashboard-leads-filter">
            <button
              type="button"
              onClick={() => setRecentLeadsFilter("recent")}
              className={`dashboard-filter-btn ${recentLeadsFilter === "recent" ? "active" : ""}`}
            >
              Recent
            </button>
            <button
              type="button"
              onClick={() => setRecentLeadsFilter("unread")}
              className={`dashboard-filter-btn ${recentLeadsFilter === "unread" ? "active" : ""}`}
            >
              Unread {!statsLoader && stats.unreadLeads > 0 && `(${stats.unreadLeads})`}
            </button>
          </div>
        </div>
        {recentLeadsLoader ? (
          <div className="dashboard-leads-list-wrap dashboard-widget-body rounded-xl border border-slate-200 bg-white p-4">
            <Skeleton count={4} height={44} className="rounded-lg" />
          </div>
        ) : recentLeads.length === 0 ? (
          <div className={`dashboard-leads-empty dashboard-widget-body rounded-xl border border-slate-200 ${recentLeadsFilter === "unread" ? "dashboard-leads-empty-caught" : "dashboard-leads-empty-default"}`}>
            {recentLeadsFilter === "unread" ? (
              <>
                <div className="dashboard-leads-empty-graphic" aria-hidden>
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="28" fill="#d1fae5" stroke="#a7f3d0" strokeWidth="2" />
                    <path d="M20 32l8 8 16-16" stroke="#059669" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="dashboard-leads-empty-title">All caught up</h3>
                <p className="dashboard-leads-empty-text">No unread leads right now. New ones will show here.</p>
                <NavLink to="/jobs" className="dashboard-leads-empty-cta">
                  View scouts
                </NavLink>
              </>
            ) : (
              <>
                <div className="dashboard-leads-empty-graphic dashboard-leads-empty-graphic-default" aria-hidden>
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="28" fill="#f0fdf4" stroke="#bbf7d0" strokeWidth="2" />
                    <path d="M32 22v20M22 32h20" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="dashboard-leads-empty-title">No leads yet</h3>
                <p className="dashboard-leads-empty-text">Create a scout to start finding leads.</p>
                <button
                  type="button"
                  className="dashboard-leads-empty-cta"
                  onClick={() => setRecentLeadsFilter("recent")}
                >
                  See recent
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="dashboard-leads-list-wrap dashboard-widget-body rounded-xl border border-slate-200 bg-white overflow-hidden">
            <ul className="dashboard-leads-list">
              {recentLeads.map((lead) => {
                const isLinkedIn = lead.type === "linkedin";
                const isUnread = !lead.is_read;
                const timeAgo = getRelativeTime(lead.post_created_at);
                const subLabel = !isLinkedIn && lead.subreddit_name
                  ? (lead.subreddit_name.startsWith("r/") ? lead.subreddit_name : `r/${lead.subreddit_name}`)
                  : null;
                const viewUrl = lead.postLink || lead.linkedInProfileURL || "#";
                return (
                  <li
                    key={lead._id}
                    className={`dashboard-lead-row ${isUnread ? "dashboard-lead-row-unread" : "dashboard-lead-row-read"} dashboard-lead-row-${lead.type || "reddit"}`}
                    onClick={() => {
                      setExpandedLeadId(lead._id);
                      if (!lead.is_read) markLeadRead(lead._id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setExpandedLeadId(lead._id);
                        if (!lead.is_read) markLeadRead(lead._id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={isLinkedIn ? `View ${lead.user || "lead"}` : `View ${lead.postTitle || "lead"}`}
                  >
                    <div className="dashboard-lead-row-inner">
                      <span className="dashboard-lead-dot-slot">
                        {isUnread ? <span className="dashboard-lead-dot" aria-hidden /> : null}
                      </span>
                      <div className="dashboard-lead-main">
                        {isLinkedIn ? (
                          <>
                            <div className="dashboard-lead-title-line">
                              <span className={`dashboard-lead-title ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={lead.user || "—"}>
                                {lead.user || "—"}
                              </span>
                              {isUnread && <span className="dashboard-lead-pill-new">NEW</span>}
                            </div>
                            {lead.bodySnippet && (
                              <p className="dashboard-lead-preview line-clamp-2" title={lead.bodySnippet}>
                                {lead.bodySnippet}{lead.bodySnippet.length >= 120 ? "…" : ""}
                              </p>
                            )}
                          </>
                        ) : (
                          <div className="dashboard-lead-title-line">
                            <span className={`dashboard-lead-title ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={lead.postTitle || lead.user || "Lead"}>
                              {lead.postTitle || lead.user || "Lead"}
                            </span>
                            {isUnread && <span className="dashboard-lead-pill-new">NEW</span>}
                          </div>
                        )}
                        <span className="dashboard-lead-meta">
                          <span className="dashboard-lead-source-icon" title={isLinkedIn ? "LinkedIn" : "Reddit"}>
                            <FontAwesomeIcon icon={isLinkedIn ? faLinkedin : faReddit} className={isLinkedIn ? "dashboard-lead-icon-linkedin" : "dashboard-lead-icon-reddit"} />
                          </span>
                          {isLinkedIn ? (
                            <><span>{lead.jobName}</span><span className="dashboard-lead-meta-sep">·</span><span className="dashboard-lead-date">{timeAgo}</span></>
                          ) : (
                            <>
                              {subLabel && <span>{subLabel}</span>}
                              {subLabel && <span className="dashboard-lead-meta-sep">·</span>}
                              <span>{lead.jobName}</span>
                              <span className="dashboard-lead-meta-sep">·</span>
                              <span className="dashboard-lead-date">{timeAgo}</span>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="dashboard-lead-actions" onClick={(e) => e.stopPropagation()}>
                        <a
                          href={viewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dashboard-lead-btn dashboard-lead-btn-view"
                        >
                          View
                        </a>
                        {isUnread && (
                          <button
                            type="button"
                            onClick={() => markLeadRead(lead._id)}
                            className="dashboard-lead-btn dashboard-lead-btn-markread"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="dashboard-leads-list-footer">
              <NavLink to="/jobs" className="dashboard-leads-list-link">
                View all scouts →
              </NavLink>
            </div>
          </div>
        )}
      </section>

      {/* Leads over time widget */}
      <section className="dashboard-widget dashboard-widget-chart">
        <div className="dashboard-widget-head">
          <h2 className="dashboard-widget-title">Leads (14 days)</h2>
          {!statsLoader && (
            <span className="dashboard-batch-inline">
              Today {batchToday} · 7d {batchLast7}
            </span>
          )}
        </div>
        {statsLoader ? (
          <div className="dashboard-widget-body rounded-xl border border-slate-200 bg-white p-4 h-[160px]">
            <Skeleton height={220} className="rounded-lg" />
          </div>
        ) : chartData.length > 0 ? (
          <div className="dashboard-chart-wrap dashboard-widget-body rounded-xl border border-slate-200 bg-white p-3">
            <div className="dashboard-chart-inner">
              <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="shortDate"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  stroke="#94a3b8"
                />
                <YAxis
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  stroke="#94a3b8"
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [value, "Leads"]}
                  labelFormatter={(label) => (label ? `Date: ${label}` : "")}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={{ fill: "#059669", strokeWidth: 0 }}
                  activeDot={{ r: 4, fill: "#059669" }}
                  name="Leads"
                />
              </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="dashboard-widget-body rounded-xl border border-slate-200 bg-slate-50/50 p-4 text-center min-h-[120px] flex items-center justify-center">
            <p className="text-slate-600 text-sm">No leads in last 14 days.</p>
          </div>
        )}
      </section>

      {/* Recent scouts widget */}
      <section className="dashboard-widget dashboard-widget-scouts">
        <div className="dashboard-widget-head">
          <h2 className="dashboard-widget-title">Recent scouts</h2>
          <NavLink to="/create-job" className="text-sm font-medium text-emerald-600 hover:text-emerald-700">+ New scout</NavLink>
        </div>

        {jobLoader ? (
          <div className="dashboard-widget-body rounded-xl border border-slate-200 bg-white p-4">
            <Skeleton count={4} height={40} className="rounded" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="dashboard-widget-body rounded-xl border border-slate-200 bg-white p-4 text-center min-h-[120px] flex flex-col items-center justify-center">
            <p className="text-slate-600 text-sm">No scouts yet.</p>
            <NavLink to="/create-job" className="mt-2 text-sm font-medium text-emerald-600 hover:text-emerald-700">Create scout</NavLink>
          </div>
        ) : (
          <div className="dashboard-table-wrap dashboard-widget-body dashboard-table-scroll">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th className="col-name">Name</th>
                  <th className="col-type">Type</th>
                  <th className="col-leads">Leads today</th>
                  <th className="col-date">Last run</th>
                  <th className="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/job-detail?job_id=${job._id}`)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && navigate(`/job-detail?job_id=${job._id}`)
                    }
                    className="cursor-pointer"
                  >
                    <td className="col-name truncate font-medium text-slate-900" title={job.jobName}>
                      {job.jobName}
                    </td>
                    <td className="col-type text-slate-600">
                      {job.type === "linkedin" && (
                        <FontAwesomeIcon icon={faLinkedin} className="text-[#0077b5]" />
                      )}
                      {job.type === "reddit" && (
                        <FontAwesomeIcon icon={faReddit} className="text-[#ff4500]" />
                      )}
                    </td>
                    <td className="col-leads text-slate-600 font-medium">
                      {job.postCountToday ?? job.postCount ?? 0}
                    </td>
                    <td className="col-date text-slate-600">
                      {job.lastExecuted && job.lastExecuted !== "1970-01-01T00:00:00.000Z"
                        ? new Date(job.lastExecuted).toLocaleString([], dateOpts)
                        : "—"}
                    </td>
                    <td className="col-action" onClick={(e) => e.stopPropagation()}>
                      <NavLink
                        to={`/job-detail?job_id=${job._id}`}
                        className="dashboard-scout-view-icon"
                        title="View scout"
                      >
                        <FontAwesomeIcon icon={faArrowRight} />
                      </NavLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </div>

      {expandedLeadId && (() => {
        const lead = recentLeads.find((l) => String(l._id) === String(expandedLeadId));
        return (
          <PostModal
            post={lead ?? null}
            onClose={() => setExpandedLeadId(null)}
            isLinkedIn={lead?.type === "linkedin"}
            showAiOutreach={true}
            jobObjective={lead?.objective ?? ""}
            accessToken={user?.accessToken}
            onMarkRead={markLeadRead}
          />
        );
      })()}
    </div>
  );
}

export default Dashboard;
